import React, { useEffect, useState } from 'react';
import { Empty, Image, Popconfirm, Space, Spin, Tag, Tooltip, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { ingestionApi } from '../ingestionApi';
import { catalogImageUrl } from '../../images';

const THUMB = 112;

/**
 * The "Product images" tab of a variant's card: the catalog thumbnail and the scraped gallery.
 * A click opens the image; the bin removes a scraped image from the variant for
 * good (it leaves every gallery and is not published to the catalog).
 */
export function VariantImages({ itemId, variant, refreshTick }) {
    const [candidates, setCandidates] = useState(null);
    const [removing, setRemoving] = useState(null);

    const load = async () => {
        try {
            const { candidates: urls } = await ingestionApi.getVariantImages(itemId, variant.id);
            setCandidates(urls || []);
        } catch (e) {
            message.error(e.message || 'Failed to load the product images');
            setCandidates([]);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId, variant.id, refreshTick]);

    const remove = async (url) => {
        setRemoving(url);
        try {
            await ingestionApi.excludeVariantImage(itemId, variant.id, url);
            setCandidates((prev) => (prev || []).filter((u) => u !== url));
            message.success('Image removed from the product');
        } catch (e) {
            message.error(e.message || 'Failed to remove the image');
        } finally {
            setRemoving(null);
        }
    };

    const cutoutUrl = variant.imageId ? catalogImageUrl(variant.imageId, 512) : null;
    if (candidates === null) {
        return <Spin />;
    }
    if (!cutoutUrl && candidates.length === 0) {
        return <Empty description="No product images" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }
    return (
        <Image.PreviewGroup>
            <Space wrap size={10} align="start">
                {cutoutUrl && (
                    <div style={{ position: 'relative' }}>
                        <Image src={cutoutUrl} alt="catalog thumbnail" width={THUMB} height={THUMB} style={{ objectFit: 'cover', borderRadius: 6 }} />
                        <Tag color="blue" style={{ position: 'absolute', top: 4, left: 4, marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                            Catalog thumbnail
                        </Tag>
                    </div>
                )}
                {candidates.map((url) => (
                    <div key={url} style={{ position: 'relative' }}>
                        <Image src={url} alt="product" width={THUMB} height={THUMB} style={{ objectFit: 'cover', borderRadius: 6 }} />
                        <Popconfirm
                            title="Remove this image?"
                            description="It leaves the product for good and will not be uploaded to the catalog."
                            okText="Remove"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => remove(url)}
                        >
                            <Tooltip title="Remove from the product">
                                <span
                                    role="button"
                                    aria-label="Remove image"
                                    style={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        width: 24,
                                        height: 24,
                                        borderRadius: 12,
                                        background: 'rgba(0,0,0,0.6)',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        opacity: removing === url ? 0.5 : 1,
                                    }}
                                >
                                    <DeleteOutlined />
                                </span>
                            </Tooltip>
                        </Popconfirm>
                    </div>
                ))}
            </Space>
        </Image.PreviewGroup>
    );
}
